/**
 * Preferences domain — React Query hooks.
 *
 * Read hooks are pass-through re-exports of the generated client.
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `PreferencesMutationId` registry in `./invalidation`.
 *
 * The mutation wrappers accept `options` so callers can pass
 * `onMutate` / `onError` for optimistic-update flows (see
 * `useInvalidatingMutation` for the supported subset).
 */

import { changeMiscSettings } from '../generated/clients/changeMiscSettings'
import { updateReportingUnit } from '../generated/clients/updateReportingUnit'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

import type {
  ChangeMiscSettingsMutationRequest,
  ChangeMiscSettingsPathParams,
} from '../generated/types/ChangeMiscSettings'
import type { UpdateReportingUnitMutationRequest } from '../generated/types/UpdateReportingUnit'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useGetPreferences,
  getPreferencesQueryKey,
  useGetPublicFeatureFlags,
  getPublicFeatureFlagsQueryKey,
} from '../generated/hooks/preferencesController'

// ── Generated query/mutation parameter & response type re-exports ──

export type { GetPreferencesQueryResponse } from '../generated/types/GetPreferences'
export type { GetPublicFeatureFlagsQueryResponse } from '../generated/types/GetPublicFeatureFlags'
export type {
  ChangeMiscSettingsMutationRequest,
  ChangeMiscSettingsMutationResponse,
  ChangeMiscSettingsPathParams,
} from '../generated/types/ChangeMiscSettings'
export type {
  UpdateReportingUnitMutationRequest,
  UpdateReportingUnitMutationResponse,
} from '../generated/types/UpdateReportingUnit'

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useChangeMiscSettings() {
  return useInvalidatingMutation('changeMiscSettings', invalidationRegistry, {
    mutationFn: (variables: {
      key: ChangeMiscSettingsPathParams['key']
      data: ChangeMiscSettingsMutationRequest
    }) => changeMiscSettings({ key: variables.key }, variables.data),
  })
}

export function useUpdateReportingUnit() {
  return useInvalidatingMutation('updateReportingUnit', invalidationRegistry, {
    mutationFn: (variables: { data: UpdateReportingUnitMutationRequest }) =>
      updateReportingUnit(variables.data),
  })
}
